'use client'

// Post-flow widgets — ported from the prototype postwidgets.jsx
// (PhotoThumb, MiniMap, ConfettiBurst, WPublished; chat widgets skipped — Q5).
import * as React from 'react'
import { useRouter } from 'next/navigation'
import { LayoutDashboard, MapPin, PartyPopper, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PALETTES, pillBtnBase } from '@/components/roostr'
import { useI18n } from '@/lib/i18n'
import type { DraftPhoto } from './post-data'

// ── Photo thumb — placeholder block + room label + quality treatments ───────
export interface PhotoThumbProps {
  photo: DraftPhoto
  size?: number
  showLabel?: boolean
  cover?: boolean
  enhanced?: boolean
  dimmed?: boolean
}

export function PhotoThumb({ photo, size = 76, showLabel = true, cover, enhanced, dimmed }: PhotoThumbProps) {
  const filt = photo.dark && !enhanced ? 'brightness(0.55) saturate(0.8)' : enhanced ? 'brightness(1.12) saturate(1.08)' : 'none'
  return (
    <div className="shrink-0 transition-opacity duration-300" style={{ width: size, opacity: dimmed ? 0.35 : 1 }}>
      <div
        className="relative overflow-hidden rounded-[10px]"
        style={{ outline: cover ? '2px solid rgb(var(--primary))' : 'none', outlineOffset: 1.5 }}
      >
        {/* Prototype PhotoBlock renders the seeded warm gradient; the roostr
            primitive draws a Building2 watermark that reads wrong at 68px, so
            the thumb paints the gradient directly (same palette math). */}
        <GradientThumb palette={photo.palette} seed={photo.id.charCodeAt(1)} filter={filt} />
        {cover ? (
          <span className="absolute left-1 top-1 rounded-full bg-primary px-[6px] py-[2px] text-[8.5px] font-extrabold tracking-[0.5px] text-white">
            COVER
          </span>
        ) : null}
        {enhanced ? (
          <span
            title="Auto-enhanced"
            className="absolute right-1 top-1 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[rgba(26,23,23,0.7)]"
          >
            <Sparkles size={11} color="#FCD34D" />
          </span>
        ) : null}
      </div>
      {showLabel ? (
        <div className="mt-[3px] overflow-hidden text-ellipsis whitespace-nowrap text-center text-[10px] text-ink-muted">
          {photo.room}
        </div>
      ) : null}
    </div>
  )
}

function GradientThumb({ palette, seed, filter }: { palette: number; seed: number; filter: string }) {
  const p = PALETTES[(palette + seed) % PALETTES.length]
  const angle = 115 + ((palette * 31 + seed * 47) % 70)
  return (
    <div
      className="aspect-square w-full transition-[filter] duration-500 ease-standard"
      style={{ background: `linear-gradient(${angle}deg, ${p[0]}, ${p[1]})`, filter }}
    />
  )
}

// ── Fake verified mini-map (gradient map w/ roads + park + pin) ──────────────
export function MiniMap({ size = 84 }: { size?: number }) {
  return (
    <div
      className="relative shrink-0 overflow-hidden rounded-shape-sm"
      style={{ width: size, height: size, background: 'linear-gradient(135deg, #EDE8DF, #DDD4C4)' }}
    >
      <div className="absolute bottom-0 top-0 w-[7px] rotate-[14deg] bg-white/75" style={{ left: '20%' }} />
      <div className="absolute left-0 right-0 h-[5px] -rotate-6 bg-white/75" style={{ top: '58%' }} />
      <div
        className="absolute rounded-[6px]"
        style={{ right: '8%', top: '10%', width: '32%', height: '30%', background: 'rgba(160,190,140,0.55)' }}
      />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[60%]">
        <MapPin size={26} className="text-primary" fill="currentColor" />
      </div>
    </div>
  )
}

// ── Confetti burst over the published card ──────────────────────────────────
export function ConfettiBurst() {
  const dots = Array.from({ length: 18 })
  const colors = ['rgb(var(--primary))', '#F59E0B', '#22C55E', '#3B82F6', '#FCA5A5']
  return (
    <div className="relative h-0" aria-hidden="true">
      {dots.map((_, i) => (
        <span
          key={i}
          className="confetti-dot"
          style={
            {
              background: colors[i % colors.length],
              left: (8 + (i * 47) % 84) + '%',
              animationDelay: i * 45 + 'ms',
              '--cx': ((i % 2 ? 1 : -1) * (14 + (i * 13) % 42)) + 'px',
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  )
}

// ── "You're live!" card shown after publish ─────────────────────────────────
export function WPublished({ onAgain }: { onAgain: () => void }) {
  const { t } = useI18n()
  const router = useRouter()
  return (
    <div className="relative animate-card-in rounded-shape-md border border-outline-variant bg-surface p-[14px] text-center shadow-e1 [overflow:visible]">
      <ConfettiBurst />
      <div className="mx-auto mb-[10px] mt-1 flex h-[54px] w-[54px] items-center justify-center rounded-full bg-primary-container">
        <PartyPopper size={26} className="text-on-primary-container" />
      </div>
      <div className="text-[16.5px] font-bold">{t('post.publishedTitle', 'You’re live!')}</div>
      <p className="mx-auto mb-[14px] mt-[5px] max-w-[300px] text-[12.5px] text-ink-secondary">
        {t(
          'post.publishedBody',
          'Your listing is in front of 1,842 people searching Vinohrady right now. First saves usually land within hours.'
        )}
      </p>
      <div className="flex justify-center gap-2">
        <button
          type="button"
          className={cn(pillBtnBase, 'bg-primary px-[18px] py-[10px] text-[13.5px] text-white')}
          onClick={() => router.push('/my-listings')}
        >
          <LayoutDashboard size={16} /> {t('post.openMyListings', 'Open My listings')}
        </button>
        <button
          type="button"
          className={cn(pillBtnBase, 'bg-surface-high px-4 py-[10px] text-[13.5px] text-ink-secondary')}
          onClick={onAgain}
        >
          {t('post.postAnother', 'Post another')}
        </button>
      </div>
    </div>
  )
}
